import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, theme, SimpleButton, Typo, ButtonLink, Loading } from '../../ui';
import { setCourse, useCourseById, useCourseProgress } from '../course/hooks';
import {
  useInitializeCourseStructure,
  useUpdateCourseProgress,
  useResetViewCourse,
  useResetViewCourseCompletely
} from './hooks';
import { useNewViewCourseId, useNewViewPageId } from '../../hooks/readFromUrls';
import { drawerWidthResponsive, drawerWidthResponsiveCompact } from '../../utils/constants';
import { media } from '../../utils/media';
import ThreeColumnLayout from '../../components/ThreeColumnLayout';
import TocMenu from './components/TocMenu';
import CoursePage from './components/CoursePage';
import { getDarkerThemeColor, getLighterThemeColor, getUserThemeColor, toRgba } from '../../ui/theme/colors';
import NotesContent from './components/NotesDrawer/NotesContent';
import { useNotesSliderToggle, useSetNotesSliderToggle } from '../global/hooks';
import NotesMobile from './components/NotesDrawer/NotesMobile';
import usePageNavigation from '../../utils/pageNavigation';
import useSwipe from './components/useSwipes';
import {
  useCourseSectionById,
  useCoursePageById,
  useCurrentPageId,
  useLastUnlockedPageId,
  useNextAvailablePageId,
  useTocMenuIsClosed,
  useCoursePages,
  useSetActiveSectionId,
  useSetCurrentPageId,
  useToggleTocMenuIsClosed
} from './hooks';
import NavButtons from './components/NavButtons'
import { useClearPage, useGetPageByIdQuery, useSetPageData, usePageTracking, useAutoPageSelection } from './hooks';
import { isPageLockedByProgress } from "../../utils/generateCourseStructure";
import { useFeatures } from '../settings/hooks';

const LockedSectionMessage = styled.div`
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  color: ${theme.colors.NEU_600};
`;

const LoadingWrapper = styled.div`
  padding: 20px;
  text-align: center;
`;

const ContentWrapper = styled(Flex)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ButtonWrapper = styled.div`
  position: absolute;
  top: calc(${theme.headerSize} + 45px);
  left: ${({$minimizedMenu, $useCompactWidth}) => {
    if ($minimizedMenu) return '15px';
    // Use compact width when both panels are open
    const width = $useCompactWidth ? drawerWidthResponsiveCompact : drawerWidthResponsive;
    return `calc(${width} - 25px)`;
  }};
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 20px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-shadow: 4px 0 8px ${toRgba(theme.colors.NEU_999, 0.05)};
  z-index: 90;
  transition: left 0.2s ease-in-out;

  ${media.lessThan('tablet')} {
    display: none;
  }
`

const ViewCourse = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlPageId = useNewViewPageId();
  const courseId = useNewViewCourseId();
  const currentPageId = useCurrentPageId();
  const isNotesOpen = useNotesSliderToggle();
  const isTocMenuClosed = useTocMenuIsClosed();
  const lastUnlockedPageId = useLastUnlockedPageId();
  const nextAvailablePageId = useNextAvailablePageId();
  const pages = useCoursePages();
  const features = useFeatures();

  // ViewCourse action hooks
  const resetViewCourse = useResetViewCourse();
  const resetViewCourseCompletely = useResetViewCourseCompletely();
  const initializeCourseStructure = useInitializeCourseStructure();
  const updateCourseProgress = useUpdateCourseProgress();
  const setCurrentPageId = useSetCurrentPageId();
  const setActiveSectionId = useSetActiveSectionId();
  const toggleTocMenuIsClosed = useToggleTocMenuIsClosed();
  const clearPage = useClearPage();
  const setPageData = useSetPageData();
  const setNotesSliderToggle = useSetNotesSliderToggle();

  const [nextLocked, setNextLocked] = useState(false);
  const [isCourseLoaded, setIsCourseLoaded] = useState(false);
  const [previousCourseId, setPreviousCourseId] = useState(null);
  const skipPageQuery = !currentPageId || currentPageId === 'diploma' || !isCourseLoaded;
  const { data: pageData } = useGetPageByIdQuery(
    { pageId: currentPageId },
    {
      skip: skipPageQuery,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const { course: courseData, isLoading: isCourseLoading, isFetching: isCourseFetching, error: courseError } = useCourseById(courseId);
  const { progress: progressData, refetch: refetchProgress } = useCourseProgress(courseId);

  // Redirect to dashboard if API returns 403 Forbidden
  useEffect(() => {
    if (courseError && courseError.status === 403) {
      // User is forbidden from accessing this course, redirect to dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [courseError, navigate]);

  // Get the page from viewCourse state (which has sectionId) instead of API pageData
  const viewCoursePage = useCoursePageById(currentPageId);
  const section = useCourseSectionById(viewCoursePage?.sectionId);
  const gated = useMemo(() => courseData?.settings?.dripContent?.gatedAccess === true, [courseData]);

  // Redirect if user tries to access a locked page directly via URL
  useEffect(() => {
    // Only check after course is loaded to avoid premature redirects
    if (isCourseLoaded && viewCoursePage && currentPageId && currentPageId !== 'diploma') {
      const isPageLocked = viewCoursePage.isLocked || section?.isLocked;

      if (isPageLocked) {
        // Redirect to last unlocked page, or next available page, or first page
        const redirectPageId = lastUnlockedPageId || nextAvailablePageId;

        if (redirectPageId && redirectPageId !== currentPageId) {
          navigate(`/view/course/${courseId}/${redirectPageId}`, { replace: true });
        }
      }
    }
  }, [isCourseLoaded, viewCoursePage, currentPageId, section, lastUnlockedPageId, nextAvailablePageId, courseId, navigate]);

  // Smart cache invalidation and reset when courseId changes
  useEffect(() => {
    if (courseId) {
      const isDifferentCourse = previousCourseId && String(previousCourseId) !== String(courseId);
      if (isDifferentCourse) { clearPage(); }

      if (!previousCourseId || isDifferentCourse) {
        resetViewCourse();
        setIsCourseLoaded(false);
        resetTracking(); // Reset page tracking when course changes
        setNextLocked(false);
      }

      setPreviousCourseId(courseId);
    }
  }, [courseId, previousCourseId, resetViewCourse, clearPage]);

  useEffect(() => {
    return () => {
      resetViewCourseCompletely('component-unmount');
    };
  }, [resetViewCourseCompletely]);

  useEffect(() => {
    if (courseData && !isCourseLoaded) {
      dispatch(setCourse(courseData));
      initializeCourseStructure(courseData);
      setIsCourseLoaded(true);
    }
  }, [courseData, isCourseLoaded, initializeCourseStructure, dispatch, courseId]);

  useEffect(() => {
    if (progressData && isCourseLoaded) {
      updateCourseProgress(progressData);
    }
  }, [progressData, isCourseLoaded, updateCourseProgress]);

  useEffect(() => {
    // Only sync urlPageId to currentPageId if we have a loaded course
    // This prevents restoring old pageId when switching courses
    if(urlPageId && String(urlPageId) !== String(currentPageId) && isCourseLoaded){
      setCurrentPageId(urlPageId);
    }
  }, [setCurrentPageId, urlPageId, currentPageId, isCourseLoaded]);

  useEffect(()=>{
    if(viewCoursePage?.sectionId) {
      setActiveSectionId(viewCoursePage.sectionId);
    }
  }, [currentPageId, viewCoursePage?.sectionId, setActiveSectionId]);

  useEffect(()=>{
    pageData?.sectionId && setActiveSectionId(pageData.sectionId)
  }, [pageData, setActiveSectionId]);

  useEffect(() => {
    if (pageData && currentPageId && String(pageData.id) === String(currentPageId)) {
      setPageData(pageData);
    }
  }, [pageData, currentPageId, setPageData]);

  // Use shared hook for page tracking (marks pages as viewed and refetches progress)
  const { resetTracking } = usePageTracking({
    pageId: currentPageId,
    courseId,
    pageData,
    isLocked: pageData?.isLocked || false,
    onSuccess: refetchProgress, // Refetch progress after marking as viewed
  });

  useEffect(() => {
    if (currentPageId === 'diploma') {
      refetchProgress();
    }
  }, [currentPageId, refetchProgress]);

  const toAvailablePage = () => {
    navigate(`/view/course/${courseId}/${lastUnlockedPageId}`, { replace: true });
  }

  // Use shared hook for automatic page selection
  const { hasRedirectedToInitialPage } = useAutoPageSelection({
    isLoading: isCourseLoading,
    isFetching: isCourseFetching,
    urlPageId,
    courseData,
    navigate,
    courseId,
    setCurrentPageId,
    basePath: '/view/course'
  });

  const serverLastViewedPageId = courseData?.lastViewedPageId;

  useEffect(() => {
    // Only fallback to currentPageId when server doesn't provide lastViewedPageId
    // This is a viewCourse-specific fallback for legacy behavior
    if (
      !isCourseLoading &&
      !isCourseFetching &&
      !urlPageId &&
      !!pages && pages?.length > 0 &&
      currentPageId &&
      !hasRedirectedToInitialPage &&
      !serverLastViewedPageId
    ) {
      navigate(`/view/course/${courseId}/${currentPageId}`, { replace: true });
    }
  }, [isCourseLoading, isCourseFetching, urlPageId, pages, courseId, navigate, currentPageId, hasRedirectedToInitialPage, serverLastViewedPageId]);

  const { prev, next } = usePageNavigation(courseData?.toc || [], currentPageId);

  const isPageLocked = useCallback((pageId) => {
    const page = pages?.find(p => p.id === pageId);
    return page?.isLocked;
  }, [pages]);

  const goToPage = useCallback((pageId) => {
    if (pageId === 'diploma' || !isPageLocked(pageId)) {
      navigate(`/view/course/${courseId}/${pageId}`, { replace: true });
    }
  },[courseId, isPageLocked, navigate]);

  const handleKeyPress = useCallback((event) => {
    const { activeElement } = document;

    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      return;
    }

    if(event.key === 'ArrowRight' && next?.id && !nextLocked) goToPage(next?.id)
    else if(event.key === 'ArrowLeft' && prev?.id) goToPage(prev?.id)
  }, [next, prev, goToPage, nextLocked])

  useEffect(()=>{
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress])

  const handleOpenNotes = () => {
    setNotesSliderToggle(!isNotesOpen);
  };

  const themeColor = useMemo(() => getUserThemeColor(courseData?.theme?.color), [courseData?.theme?.color]);// eslint-disable-line react-hooks/exhaustive-deps
  const lightColor = useMemo(() => getLighterThemeColor(themeColor), [themeColor]);
  const darkColor = useMemo(() => getDarkerThemeColor(themeColor), [themeColor]);

  const allSectionsLocked = useMemo(() => {
    const allIsLocked = courseData?.toc?.filter((section)=>section.id !== 'Diploma').every((section) => section.isLocked);
    return allIsLocked;
  }, [courseData]);

  const handleSwipeRight = useCallback(() => {
    if(prev?.id)
      goToPage(prev?.id)
  }, [prev, goToPage]);

  const handleSwipeLeft = useCallback(() => {
    if(next?.id && !nextLocked)
      goToPage(next?.id);
  }, [next, goToPage, nextLocked]);

  const isQuiz = useMemo(() => {
    // we have some bugs in place when duplicating courses that makes quiz pages to disapear. get this back when we fix this.
    // const hasQuiz = course?.settings?.quiz?.enabled && course?.settings?.quiz?.pages?.hasOwnProperty(urlPageId);
    const hasQuiz = courseData?.settings?.quiz?.enabled;
    return hasQuiz && section?.type === 'quiz';
  }, [courseData, section]);

  useSwipe(handleSwipeLeft, handleSwipeRight);

  const toggleMenuOpen = () => {
    toggleTocMenuIsClosed()
  }

  const nextId = next?.id;

  useEffect(() => {
    if (gated && nextId && pages) {
      const locked = isPageLockedByProgress({ pageId: nextId, pages, isGated: true });

      const nextPage = pages.find(p => p.id === nextId);
      const nextSectionLocked = nextPage?.sectionId ?
        courseData?.toc?.find(s => s.id === nextPage.sectionId)?.isLocked : false;

      setNextLocked(locked || nextSectionLocked);
    }
  }, [nextId, pages, gated, currentPageId, courseData?.toc]);

  // Determine if both panels are open (only relevant on desktop/bigTablet)
  const bothPanelsOpen = !isTocMenuClosed && isNotesOpen;

  return (
    <>
      <ButtonWrapper $minimizedMenu={isTocMenuClosed} $useCompactWidth={bothPanelsOpen}>
        <SimpleButton
          icon={!!isTocMenuClosed ? 'DoubleArrowRight' : 'DoubleArrowLeft'}
          onClick={toggleMenuOpen}
          variant='ghost'
          size='small'
          noTooltip
          aria-label={!!isTocMenuClosed ? t('coursePage.expand') : t('coursePage.minimize')}
        />
        {courseData?.canEdit && !features?.modernCourseBuilder && (
          <ButtonLink
            href={`/course/${courseData?.ownerSlug}/${courseData?.slug}/${currentPageId}`}
            newTab
            variant='ghost'
            icon='Edit'
            size='small'
          />
        )}
      </ButtonWrapper>
      <ThreeColumnLayout
        isLeftPanelCompact={isTocMenuClosed}
        isRightPanelOpen={isNotesOpen}
        onLeftPanelToggle={toggleTocMenuIsClosed}
        onRightPanelToggle={setNotesSliderToggle}
        exclusivePanelsOnMedium={false}
        context="view"
        leftPanelContent={<TocMenu isExpanded={!isTocMenuClosed} />}
        rightPanelContent={<NotesContent themeColor={themeColor} />}
        mainContent={
          <ContentWrapper direction='column'>
            {currentPageId !== 'diploma' && allSectionsLocked ? (
              <LockedSectionMessage>
                <Typo.ContentSmallHeader>{t('coursePage.allLocked')}</Typo.ContentSmallHeader>
                <Typo.Text>{t('coursePage.contactAdmin')}</Typo.Text>
              </LockedSectionMessage>
            ) :
              pageData?.isLocked ?
                <LockedSectionMessage>
                  <Typo.ContentSmallHeader>{t('coursePage.pageLocked.title')}</Typo.ContentSmallHeader>
                  <Typo.Text>{t('coursePage.pageLocked.description')}</Typo.Text>
                  <SimpleButton onClick={toAvailablePage}>{t('coursePage.pageLocked.navToPage')}</SimpleButton>
                </LockedSectionMessage>
                :
                // Only show page content if we have valid page data for current page OR it's a diploma page
                (() => {
                  const hasPageData = !!pageData;
                  const hasCurrentPageId = !!currentPageId;
                  const idsMatch = pageData && currentPageId && String(pageData.id) === String(currentPageId);
                  const isDiplomaPage = currentPageId === 'diploma';
                  const shouldShow = (hasPageData && hasCurrentPageId && idsMatch) || isDiplomaPage;

                  return shouldShow;
                })() ? (
                  <>
                    <CoursePage
                      key={currentPageId} // Force re-render when page changes
                      courseId={courseId}
                      pageId={currentPageId}
                      isQuiz={isQuiz}
                      pageInfo={pageData}
                      isNotesOpen={isNotesOpen}
                      quizData={isQuiz ? courseData.settings.quiz.pages[currentPageId] : null}
                    />
                    <NotesMobile
                      fullWidth={isTocMenuClosed}
                      makeItOpen={isNotesOpen}
                      setNotesDrawerOpen={handleOpenNotes}
                      themeColor={themeColor}
                      isNotesOpen={isNotesOpen}
                    />
                    <NavButtons
                      nextLocked={nextLocked}
                      prev={prev}
                      next={next}
                      goToPage={goToPage}
                      isTocMenuClosed={isTocMenuClosed}
                      isNotesOpen={isNotesOpen}
                      lightColor={lightColor}
                      darkColor={darkColor}
                      handleOpenNotes={handleOpenNotes}
                      themeColor={themeColor}
                    />
                  </>
                ) : (
                // Show loading state when page data doesn't match current page
                <LoadingWrapper>
                  <Loading themeColor={themeColor} />
                </LoadingWrapper>
              )
            }
          </ContentWrapper>
        }
      />
    </>
  );
};

export default ViewCourse;
